import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, Pencil, Trash2, ArrowLeft, Search,
  Package
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminProducts() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/");
      return;
    }
    fetchProducts();
  }, [user, navigate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/products/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(prev => prev.filter(p => p.id !== deleteId));
      toast.success("Product deleted");
    } catch (error) {
      toast.error("Failed to delete product");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="admin-products">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <Link
              to="/admin"
              className="flex items-center gap-2 text-[#888] hover:text-[#39FF14] transition-colors mb-4"
            >
              <ArrowLeft size={18} />
              <span className="label-text">Back to Dashboard</span>
            </Link>
            <h1 className="text-4xl heading text-[#EDEDED]">Products</h1>
            <p className="text-[#888] mt-1">{products.length} products</p>
          </div>
          <Link
            to="/admin/products/new"
            className="btn-skew bg-[#39FF14] text-black px-6 py-3 font-bold uppercase text-sm flex items-center gap-2"
            data-testid="add-product-btn"
          >
            <Plus size={18} className="transform skewX-[12deg]" />
            <span>Add Product</span>
          </Link>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#888]" size={20} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full gcsr-input pl-12"
            data-testid="search-products"
          />
        </div>

        {/* Products Table */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 skeleton rounded-lg"></div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16 bg-[#0A0A0A] border border-[#222]">
            <Package size={48} className="mx-auto text-[#333] mb-4" />
            <p className="text-[#888]">
              {search ? "No products match your search" : "No products yet"}
            </p>
            {!search && (
              <Link
                to="/admin/products/new"
                className="mt-4 inline-flex btn-skew bg-[#39FF14] text-black px-6 py-3 font-bold uppercase text-sm"
              >
                <span>Add First Product</span>
              </Link>
            )}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#222] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#222]">
                    <th className="px-6 py-4 text-left text-[#888] label-text text-sm">Product</th>
                    <th className="px-6 py-4 text-left text-[#888] label-text text-sm">Category</th>
                    <th className="px-6 py-4 text-left text-[#888] label-text text-sm">Price</th>
                    <th className="px-6 py-4 text-left text-[#888] label-text text-sm">Stock</th>
                    <th className="px-6 py-4 text-left text-[#888] label-text text-sm">Status</th>
                    <th className="px-6 py-4 text-right text-[#888] label-text text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr
                      key={product.id}
                      className="border-b border-[#222] hover:bg-[#111] transition-colors"
                      data-testid={`product-row-${product.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-12 h-12 object-cover"
                          />
                          <span className="text-[#EDEDED] font-semibold">{product.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[#888] capitalize">{product.category}</td>
                      <td className="px-6 py-4 text-[#39FF14] heading">${product.price.toFixed(2)}</td>
                      <td className="px-6 py-4 text-[#EDEDED]">{product.stock}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {product.is_new_arrival && (
                            <Badge className="bg-[#39FF14] text-black label-text hover:bg-[#39FF14]">New</Badge>
                          )}
                          {product.is_featured && (
                            <Badge className="bg-[#FF00FF] text-white label-text hover:bg-[#FF00FF]">Featured</Badge>
                          )}
                          {!product.is_new_arrival && !product.is_featured && (
                            <span className="text-[#888]">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/products/${product.id}/edit`}
                            className="p-2 text-[#888] hover:text-[#39FF14] transition-colors"
                            data-testid={`edit-${product.id}`}
                          >
                            <Pencil size={18} />
                          </Link>
                          <button
                            onClick={() => setDeleteId(product.id)}
                            className="p-2 text-[#888] hover:text-red-500 transition-colors"
                            data-testid={`delete-${product.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#0A0A0A] border-[#222]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#EDEDED] heading">Delete Product?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#888]">
              This action cannot be undone. This will permanently delete the product.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#333] text-[#EDEDED] hover:bg-[#111] hover:text-[#EDEDED]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
